import React, {useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Trans} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {setAccountSummary} from './reducers';
import {toast} from 'react-toastify';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import dao from '../../dao/configDao';
import accountDao from '../../dao/accountDao';
import authDao from '../../dao/authDao';
import InfoDialog from '../dialogs/InfoDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import ModalContent from '../../pages/auth/modalcontent';
import {
  getBrandId,
  listDevices,
  migrateTemplateV2,
  processV1toV2Data,
} from '../../utils/skincareUtils';
import smbUtils from '../../utils/smbUtils';
import subscriptionUtils from '../../utils/subscriptionUtils';
import appVersionUtils from '../../utils/appVersionUtils';
import storeUtils from '../../utils/storeUtils';
import UpgradeModal4ScpV1 from './UpgradeModal4ScpV1';
import UpgradeModal4ScpV2 from './UpgradeModal4ScpV2';
import skincareProDao from '../../dao/skincareProDao';
import UpgradeCompletedModal from './UpgradeCompletedModal';

export default function UpgradeModal({show, setShow}) {
  const auth = useSelector(state => state.components.auth.accountSummary);
  const functionType = _get(auth, 'customer.functionType');
  const {token, customerId} = smbUtils.customerInfo(auth);
  const [step, setStep] = useState(0);
  const [versionFrom, setVersionFrom] = useState(
    subscriptionUtils.getSkincareVersion(auth)
  );
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brandInfo, setBrandInfo] = useState(null);
  const [isAllAppVersionUpdated, setIsAllAppVersionUpdated] = useState(false);
  const [countOlderAppVersions, setCountOlderAppVersions] = useState(0);
  const allConfig = useSelector(
    state => state.pages.webmodule.configuration.allConfig
  );

  useEffect(() => {
    getBrandId(token, customerId).then(res => {
      if (res) {
        setBrandInfo(res);
      }
    });
    listDevices(token, customerId).then(res => {
      if (!res) {
        return;
      }
      setCountOlderAppVersions(
        appVersionUtils.countOlderAppVersions(
          appVersionUtils.scpAppLatestVersion,
          _get(res, 'devices', [])
        )
      );
      setIsAllAppVersionUpdated(
        appVersionUtils.isAllAppVersionsLatest(
          appVersionUtils.scpAppLatestVersion,
          _get(res, 'devices', [])
        )
      );
    });
  }, []);

  const updateScpV1ToLatest = () => {
    setLoading(true);
    processV1toV2Data(token, customerId, brandInfo).then(r => {
      migrateTemplateV2(
        token,
        customerId,
        r.landingConfigJson,
        r.landingMetaDataJson,
        r.resultConfigJson,
        r.resultMetaDataJson,
        r.commonSettingConfigJson,
        r.commonSettingMetaDataJson
      )
        .then(async r => {
          storeUtils.setUpdatedSuccess(customerId);
          storeUtils.setUpdatedSuccessFromVersion(customerId, 1);
          await updateScpV2ToLatest();
        })
        .catch(() => {
          toast.error(
            <Trans i18nKey="toast.error.update.version.failed">
              Failed to update, please try again.
            </Trans>
          );
          setLoading(false);
          setStep(0);
          setShow(false);
        });
    });
  };

  const updateScpV2ToLatest = async () => {
    setLoading(true);
    await skincareProDao
      .getEnabledHd()
      .then(r => {
        if (r && r.status && r.status === 'success') {
          setIsCompleted(true);
        } else {
          toast.error(
            <Trans i18nKey="toast.error.update.version.failed">
              Failed to update, please try again.
            </Trans>
          );
          setStep(0);
          setShow(false);
        }
      })
      .catch(error => {
        toast.error(
          <Trans i18nKey="toast.error.update.version.failed">
            Failed to update, please try again.
          </Trans>
        );
        setStep(0);
        setShow(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  switch (functionType) {
    case 'SKINCARE_PRO':
      if (isCompleted)
        return (
          <UpgradeCompletedModal
            show={show}
            setShow={setShow}
            version={versionFrom}
          />
        );

      if (versionFrom === 1) {
        return (
          <UpgradeModal4ScpV1
            show={show}
            setShow={setShow}
            step={step}
            setStep={setStep}
            update={updateScpV1ToLatest}
            loading={loading}
          />
        );
      }

      return (
        <UpgradeModal4ScpV2
          show={show}
          setShow={setShow}
          step={step}
          setStep={setStep}
          update={updateScpV2ToLatest}
          loading={loading}
          isAllAppVersionUpdated={isAllAppVersionUpdated}
          countOlderAppVersions={countOlderAppVersions}
        />
      );

    default:
      return (
        <Modal size={'lg'} show={show} backdrop="static" centered>
          <Modal.Header closeButton={false}>
            <Modal.Title>
              <div className="update-modal--title">
                <Trans i18nKey="c.l.header.update.title">
                  New Update is Available
                </Trans>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ModalContent />
            <div>
              <Trans i18nKey="c.l.header.update.bottom.title">
                <strong>You’re in Control</strong>
              </Trans>
              <br />
              <Trans i18nKey="c.l.header.update.bottom.desc">
                This upgrade is 100% safe, all your existing data, SKUs, and
                integrations will remain untouched and continue to work as-is.
              </Trans>
            </div>
          </Modal.Body>
          <Modal.Footer style={{justifyContent: 'space-between'}}>
            <InfoDialog
              trigger={'click'}
              placement="top"
              title={
                <div>
                  <Trans i18nKey="c.l.header.update.notice.title">
                    Update Notice
                  </Trans>
                </div>
              }
              description={
                <div>
                  <Trans i18nKey="c.l.header.update.notice.desc">
                    You cannot revert to the older version once upgraded. We
                    still highly recommend you go ahead and get this free
                    upgrade for the added new feature and benefits. If you
                    choose to bypass the upgrade now, you can always upgrade
                    anytime by clicking the “New Update is Available” button on
                    the top of your Perfect Console.
                  </Trans>
                </div>
              }
            >
              <a
                className="nav-link active"
                href="javascript:;"
                style={{padding: 0}}
              >
                <Trans i18nKey="c.l.header.update.notice">
                  Update Notice{' '}
                  <i className="fa fa-question-circle cursor-help" />
                </Trans>
              </a>
            </InfoDialog>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary kt-margin-r-10"
                onClick={() => {
                  storeUtils.setHideUpdatedModal(customerId, 1);
                  setShow(false);
                }}
              >
                <Trans i18nKey="c.l.header.update.button.later">
                  Update later
                </Trans>
              </button>
              <ConfirmDialog
                placement="top"
                confirmCallback={() => {
                  dao.queryConfig().then(res => {
                    accountDao
                      .upgradeCustomer(!_isEmpty(res) ? res : allConfig)
                      .then(() => window.location.reload());
                  });
                }}
              >
                <button type="button" className="btn btn-primary btn-danger">
                  <Trans i18nKey="c.l.header.update.button.now">
                    Update now
                  </Trans>
                </button>
              </ConfirmDialog>
            </div>
          </Modal.Footer>
        </Modal>
      );
  }
}
